import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, MailStatus, OutgoingEmail } from '../api';
import { ErrorBox, Field, useAsync } from './ui';

/**
 * Levélírás a csatolt postafiókból (opcionálisan ajánlat-PDF-fel). A levél sorba kerül, a háttér pár másodpercen belül elküldi;
 * itt megvárjuk az eredményt.
 */
export function EmailCompose({ initial, quoteId, subjectRef, onClose, onSent }: {
  initial: { to?: string; subject?: string; body?: string }; quoteId?: string; subjectRef?: { type: string; id: string }; onClose: () => void; onSent?: () => void;
}) {
  const [st, setSt] = useState<MailStatus | null>(null);
  const [v, setV] = useState({ to: initial.to ?? '', cc: '', subject: initial.subject ?? '', body: initial.body ?? '' });
  const [sending, setSending] = useState<OutgoingEmail | null>(null);
  const a = useAsync();
  const timer = useRef<number>();
  useEffect(() => { api<MailStatus>('/mail/status').then(setSt).catch(() => setSt(null)); return () => window.clearTimeout(timer.current); }, []);
  const list = (s: string) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
  const poll = async (id: string) => {
    const r = (await api<{ items: OutgoingEmail[] }>(`/emails/outgoing${quoteId ? `?quote_id=${quoteId}` : ''}`)).items.find((x) => x.id === id);
    if (r) setSending(r);
    if (r?.status === 'sent') { onSent?.(); return; }
    if (r?.status !== 'failed') timer.current = window.setTimeout(() => poll(id), 1500);
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const r = await a.run(() => api<{ id: string }>('/emails', { method: 'POST', body: {
      to: list(v.to), cc: list(v.cc), subject: v.subject.trim(), body: v.body, ...(quoteId ? { quote_id: quoteId } : {}), ...(subjectRef ? { subject_ref: subjectRef } : {}) } }));
    if (r) { setSending({ id: r.id, status: 'queued' } as OutgoingEmail); poll(r.id); }
  };
  const account = st?.account && st.account.status !== 'disconnected' ? st.account : null;
  return (
    <div className="modal-back" role="dialog" aria-modal="true" aria-label="Levél írása" onClick={onClose}>
      <div className="modal card wide" onClick={(e) => e.stopPropagation()}>
        <h3>Levél{quoteId ? ' az ajánlattal' : ''}</h3>
        {st && !account && <p className="notice">Nincs csatolt postafiókod. <Link to="/beallitasok/postafiok">Csatold a Google- vagy Microsoft 365-fiókodat</Link>, és innen küldhetsz.</p>}
        {sending ? (
          <div>
            {sending.status === 'sent' && <p>Elküldve. A levél a kontakt idővonalán is megjelent{quoteId ? ', az ajánlat „elküldve” állapotba került' : ''}.</p>}
            {(sending.status === 'queued' || sending.status === 'sending') && <p className="muted">Küldés folyamatban…</p>}
            {sending.status === 'failed' && <div className="error">Nem sikerült elküldeni: {sending.error}</div>}
            <div className="actions"><button onClick={onClose}>Bezárás</button></div>
          </div>
        ) : (
          <form className="form" onSubmit={submit}>
            {account && <p className="muted small">Feladó: {account.email}</p>}
            <Field label="Címzett"><input value={v.to} onChange={(e) => setV({ ...v, to: e.target.value })} required placeholder="nev@ceg.hu" /></Field>
            <Field label="Másolat" hint="opcionális, vesszővel elválasztva"><input value={v.cc} onChange={(e) => setV({ ...v, cc: e.target.value })} /></Field>
            <Field label="Tárgy"><input value={v.subject} onChange={(e) => setV({ ...v, subject: e.target.value })} required maxLength={300} /></Field>
            <Field label="Szöveg"><textarea rows={10} value={v.body} onChange={(e) => setV({ ...v, body: e.target.value })} required /></Field>
            {quoteId && <p className="muted small">Melléklet: az ajánlat PDF-je.</p>}
            <ErrorBox error={a.error} />
            <div className="actions">
              <button type="submit" disabled={a.busy || !account}>Küldés</button>
              <button type="button" className="secondary" onClick={onClose}>Mégse</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
